// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Info, ShieldAlert, X } from 'lucide-react';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

/**
 * THE CONFIRM IS ALWAYS IN REACH. A dialog's action row never scrolls away with its content: the
 * `footer` prop renders it outside the scrolling body, and a footer written at the end of the
 * body (the `-mx-5 -mb-4 mt-4` row, WizardFooter) is `sticky -bottom-4`, pinned to the dialog's
 * bottom edge while the content scrolls under it. A new dialog footer carries the same classes.
 *
 * A DIALOG, TO A KEYBOARD AND A SCREEN READER TOO (merged from the venue module's kit): focus moves into
 * it when it opens (the element marked data-autofocus, else the first field, else the first button), Tab
 * stays inside while it is open, focus returns to where it was when it closes, and Escape closes it
 * (unless a menu inside it is open, which closes first).
 *
 *   width           max width class (default max-w-[520px])
 *   sheet           on phones, a bottom sheet across the full width
 *   label           accessible name when the title is not text
 *   bodyClassName   replaces the body padding (default px-5 py-4 with a title)
 *   panelClassName  extra classes for the dialog panel (a fixed height, say)
 *   footerClassName classes for the footer row
 */
export const Modal = ({ open, onClose, children, width = 'max-w-[520px]', backdropClose = true, title, sub, footer, sheet = false, label, bodyClassName, panelClassName = '', footerClassName = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const back = document.activeElement;
    const t = setTimeout(() => {
      const node = ref.current;
      if (!node || node.contains(document.activeElement)) return;
      const el = node.querySelector('[data-autofocus]') || node.querySelector('input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])') || node.querySelector(FOCUSABLE);
      (el || node).focus();
    }, 20);
    return () => { clearTimeout(t); if (back && back.isConnected && typeof back.focus === 'function') back.focus(); };
  }, [open]);
  if (!open) return null;
  const onKeyDown = (e) => {
    const node = ref.current;
    if (e.key === 'Escape') {
      if (e.defaultPrevented || (node && node.querySelector('[role="menu"]'))) return;
      e.stopPropagation();
      if (onClose) onClose();
      return;
    }
    if (e.key !== 'Tab' || !node) return;
    const f = [...node.querySelectorAll(FOCUSABLE)].filter(x => x.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <div className={`fixed inset-0 z-[90] bg-[#1F2937]/40 flex justify-center ${sheet ? 'items-end md:items-center md:p-4' : 'items-center p-4'}`} onClick={backdropClose ? onClose : undefined}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={label || (typeof title === 'string' ? title : undefined)} tabIndex={-1} onKeyDown={onKeyDown}
        className={`bg-white border border-[#E6E4E1] w-full ${width} shadow-xl flex flex-col overflow-hidden focus:outline-none ${sheet ? 'rounded-t-[14px] md:rounded-[12px] max-h-[92vh] md:max-h-[90vh]' : 'rounded-[12px] max-h-[90vh]'} ${panelClassName}`} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="px-5 pt-5 pb-3 border-b border-[#EEEDEA] flex items-start justify-between gap-3 flex-shrink-0">
            <div className="min-w-0">
              <div className="text-[14.5px] font-semibold text-[#1F2937] leading-tight">{title}</div>
              {sub && <div className="text-[11.5px] text-[#6B7280] mt-0.5">{sub}</div>}
            </div>
            <button type="button" onClick={onClose} className="w-7 h-7 rounded-md hover:bg-[#F2F1ED] flex items-center justify-center flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2937]/60" title="Close" aria-label="Close"><X className="w-3.5 h-3.5 text-[#6B7280]" strokeWidth={2} aria-hidden="true" /></button>
          </div>
        )}
        <div className={bodyClassName != null ? `flex-1 min-h-0 overflow-y-auto ${bodyClassName}` : `${title ? 'px-5 py-4' : ''} flex-1 overflow-y-auto`}>
          {children}
        </div>
        {footer && (
          <div className={`px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] flex-shrink-0 ${sheet ? 'md:rounded-b-[12px]' : 'rounded-b-[12px]'} ${footerClassName}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// Confirm dialog — for any irreversible or sensitive action

export const ConfirmDialog = ({ open, onClose, title, body, confirmLabel = 'Confirm', tone = 'neutral', onConfirm, requireText, approval, requireReason, reasonPlaceholder = 'What needs to change before this can be approved?' }) => {
  const [text, setText] = useState('');
  const [reason, setReason] = useState('');
  // Adjust state during render (not in an effect) when `open` changes, so a closed
  // dialog's stale text/reason never flashes for a frame before an effect clears it —
  // see react.dev "Adjusting some state when a prop changes".
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) { setText(''); setReason(''); }
  }
  if (!open) return null;
  const disabled = (requireText ? text !== requireText : false) || (requireReason ? !reason.trim() : false);
  const btnTone = tone === 'danger' ? 'bg-[#9B1F1F] hover:bg-[#7F1818] text-white' : 'bg-[#1F2937] hover:bg-[#111827] text-white';
  return (
    <Modal open={open} onClose={onClose}>
      <div className="px-5 pt-5 pb-3">
        <div className={`w-9 h-9 rounded-full ${tone === 'danger' ? 'bg-[#FEE2E2] text-[#9B1F1F]' : 'bg-[#F2F1ED] text-[#1F2937]'} flex items-center justify-center mb-3`}>
          {tone === 'danger' ? <AlertTriangle className="w-4 h-4" strokeWidth={2} /> : <Info className="w-4 h-4" strokeWidth={2} />}
        </div>
        <div className="text-[15px] font-semibold text-[#1F2937] mb-1.5">{title}</div>
        <div className="text-[12.5px] text-[#4B5563] leading-relaxed">{body}</div>
        {approval && (
          <div className="mt-3 px-3 py-2 bg-[#FEF3C7] border border-[#FDE68A] rounded-md text-[11.5px] text-[#8A5A12] flex items-start gap-2">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{approval}</span>
          </div>
        )}
        {requireText && (
          <div className="mt-4">
            <div className="text-[11.5px] text-[#6B7280] mb-1.5">Type <span className="font-mono font-semibold text-[#1F2937]">{requireText}</span> to confirm.</div>
            <input value={text} onChange={(e) => setText(e.target.value)} className="w-full h-9 px-3 text-[12.5px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] font-mono" />
          </div>
        )}
        {requireReason && (
          <div className="mt-4">
            <div className="text-[11.5px] text-[#6B7280] mb-1.5">{typeof requireReason === 'string' ? requireReason : 'Reason (shown to the organizer):'}</div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={reasonPlaceholder}
              className="w-full px-3 py-2 text-[12.5px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] resize-none"
            />
          </div>
        )}
      </div>
      <div className="px-5 py-3 border-t border-[#EEEDEA] flex items-center justify-end gap-2 bg-[#FBFAF7] rounded-b-[12px]">
        <button onClick={onClose} className="h-8 px-3 rounded-md text-[12px] font-medium text-[#1F2937] hover:bg-[#F2F1ED] border border-[#E6E4E1] bg-white whitespace-nowrap">Cancel</button>
        <button disabled={disabled} onClick={() => { onConfirm && onConfirm(requireReason ? reason.trim() : undefined); onClose(); }} className={`h-8 px-3 rounded-md text-[12px] font-medium whitespace-nowrap ${btnTone} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>{confirmLabel}</button>
      </div>
    </Modal>
  );
};

// useConfirm hook — convenience wrapper
